import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { PageInfo } from '../../components/Header/PageInfo';
import { removeMatchingElementFromArray } from '../../utils/helpers';
import { useTranslation } from 'react-i18next';
import { FilterDropDown } from '../../components/Common/FilterDropDown';
import {
  Filters,
  getFilterSortInfoFromFilterKey,
} from '../../components/Common/FilterDropDown/filters';
import {
  useFetchAppsBySearchStringQuery,
  useFetchSuggestedAppsQuery,
} from '../../redux/api/appMarket-api-slice';
import { generateRoute } from '../../utils/location';
import { SearchAppsWithSearchStringParameters } from '../../redux/api/types';
import { NoResult } from '../../components/Common/NoResult'
import { AppMarketPageIdentifier } from '../../AppRoutes';
import { APP_GRID_LOAD_MORE_CONFIG, AppGrid } from '../../components/AppGrid';
import { SetPageTitle } from '../../components/Common/SetPageTitle';
import { useRatingsAndReviews } from '../../contexts/providers/ratings-reviews';
import { useAppContext } from "../../hooks/useAppContext";

let defaultFilterKey = Filters.MostRelevant;

const GetPageTitle = (q: string, numberOfResults: number = 0) => {
  const [t] = useTranslation();
  return (
    <span>
      {`${numberOfResults} ${t('app_search.search_for',{result:q})}`}
    </span>
  );
};

export const AppSearchResults = (): JSX.Element => {
  let AllowedFilters: Filters[] = [
    Filters.MostRelevant,
    Filters.TopRated,
    Filters.MostPopular,
    Filters.Newest,
  ];
  const { searchStr, filterKey } = useParams<{ searchStr: string, filterKey: Filters }>();
  let history = useHistory();
  const [limit, setLimit] = useState<number>(APP_GRID_LOAD_MORE_CONFIG.maxEntriesPerPage as number + 1);
  const ratingsAndReviewsFeatureFlag = useRatingsAndReviews();
  const searchStrInAscii = decodeURIComponent(searchStr);
  let sort = getFilterSortInfoFromFilterKey((filterKey) || defaultFilterKey);
  const merchantId = useAppContext()?.id || '';
  const queryParams: SearchAppsWithSearchStringParameters = {
    q: searchStrInAscii,
    limit,
    merchantId: merchantId
  };

  if (sort) queryParams.sorts = [sort];
  const {
    data: appSearchData,
    isLoading: isLoadingApps,
    isError: isFetchAppsInErrorState,
    isFetching: appSearchIsFetching,
  } = useFetchAppsBySearchStringQuery(queryParams);
  const { data: numberOfResults } = useFetchSuggestedAppsQuery(
    { q: searchStrInAscii, merchantId: merchantId },
    {
      selectFromResult: ({ data, isLoading }) => {
        return {
          data: data?.info?.totalResultCount || 0,
          isLoading,
        };
      },
    },
  );

  if (!ratingsAndReviewsFeatureFlag.enabled){
    removeMatchingElementFromArray<Filters>(AllowedFilters, Filters.TopRated);
    if (filterKey === Filters.TopRated) history.replace(
      generateRoute(
        AppMarketPageIdentifier.SearchedApps, [
          searchStr,
          defaultFilterKey
        ]
      )
    );
  }

  const filterChangeHandler = (filters: Filters) => {
    history.push(
      generateRoute(AppMarketPageIdentifier.SearchedApps, [searchStr, filters]),
    );
  };

  const changeLimit = (apiLimit: number) => {
    if (apiLimit !== limit) setLimit(apiLimit);
  }

  const pageTitile = GetPageTitle(searchStrInAscii, numberOfResults);

  if (!!numberOfResults) {
    return (
      <>
        <SetPageTitle/>
        <PageInfo hideDescription={true} title={pageTitile} role='alert' />
        <FilterDropDown
          onChangeHandlerCallback={filterChangeHandler}
          allowedFilters={AllowedFilters}
          selectedFilter={filterKey || defaultFilterKey}
        />
        {(appSearchData && appSearchData.ids && appSearchData.ids.length === 0) || isFetchAppsInErrorState ? (
          ''
        ) : (
          <AppGrid apps={appSearchData} isLoading={isLoadingApps} isFetching={appSearchIsFetching} setApiLimit={changeLimit}/>
        )}
      </>
    );
  }
  return <NoResult checkSearchString={searchStrInAscii}/>
};
